import React from "react";
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    Button,
    Checkbox,
    FormControlLabel, IconButton, useMediaQuery, Dialog, Link, FormHelperText, FormControl,
} from "@mui/material";
import SquareCheckbox from "../filters/squareCheckbox";
import {useAccountData} from "../../../hooks/account/useAccountData";
import {useSignUp} from "../../../hooks/signUp/useSignUp";
import CloseIcon from "@mui/icons-material/Close";
import {useTheme} from "@mui/material/styles";
import {formControlDefaults, textFieldDefaults, textFieldSelectDefaults} from "../../../helpers/theme/defaultProps";
import {LoadingButton} from "@mui/lab";
import {useTranslation} from "react-i18next";
import {PAGE_LOGIN} from "../../../locales/pages/namespaces";
import {ErrorMessage, FormikProvider} from "formik";

const FIELD_WIDTH = 286;


function ProfileTextField({
                              name,
                              label,
                              required = false,
                              value,
                              onChange,
                              onBlur
}) {
    return (
        <Box sx={{position: "relative", width: "100%", height: 59,mb:1}}>
            <FormControl
                {...formControlDefaults}
            >
            <TextField
                {...textFieldDefaults}
                fullWidth
                required={required}
                variant="outlined"
                size="small"
                name={name}
                label={label}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                InputLabelProps={{
                    sx: {
                        fontFamily: "'Zen Maru Gothic', sans-serif",
                        fontSize: 14,
                        color: "#111111",
                        "&.Mui-focused": {
                            color: "#111111",
                        },
                    },
                }}
                InputProps={{
                    sx: {
                        fontFamily: "'Zen Maru Gothic', sans-serif",
                        fontSize: 14,
                        "& .MuiInputBase-input": {
                            py: 1.1,
                            pl: 1,
                        },
                        "& fieldset": {
                            borderColor: "#111111",
                            borderWidth: 1,
                            borderRadius: "4px",
                        },
                        "&:hover fieldset": {
                            borderColor: "#111111",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#111111",
                        },
                    },
                }}
            />
                <FormHelperText>
                    <ErrorMessage name={name}/>
                </FormHelperText>
            </FormControl>
        </Box>
    );
}


function ProfileSelectField({
                                name,
                                label,
                                options,
                                required = false,
                                value,
                                onChange,
                                onBlur
                            }) {
    return (
        <Box sx={{position: "relative", width: "100%", height: 59,mb:1}}>
            <FormControl
                {...formControlDefaults}
            >
            <TextField
                {...textFieldSelectDefaults}
                select
                fullWidth
                required={required}
                variant="outlined"
                size="small"
                name={name}
                label={label}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                InputLabelProps={{
                    sx: {
                        fontFamily: "'Zen Maru Gothic', sans-serif",
                        fontSize: 14,
                        color: "#111111",
                        "&.Mui-focused": {
                            color: "#111111",
                        },
                    },
                }}
                InputProps={{
                    sx: {
                        fontFamily: "'Zen Maru Gothic', sans-serif",
                        fontSize: 14,
                        "& .MuiInputBase-input": {
                            py: 1.1,
                            pl: 1,
                        },
                        "& fieldset": {
                            borderColor: "#111111",
                            borderWidth: 1,
                            borderRadius: "4px",
                        },
                        "&:hover fieldset": {
                            borderColor: "#111111",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#111111",
                        },
                    },
                }}
            >
                {options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                    </MenuItem>
                ))}
            </TextField>
                <FormHelperText>
                    <ErrorMessage name={name}/>
                </FormHelperText>
            </FormControl>
        </Box>
    );
}

export default function SignUpForm({open, onClose, onLogin}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {formik, countryOptions, loading} = useSignUp({onClose});
    const {t} = useTranslation(PAGE_LOGIN)

    const handleClose = ()=>{
        onLogin()
            onClose()
    }

    return (
        <FormikProvider value={formik}>
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            fullWidth
            PaperProps={{
                sx: {
                    width: isMobile ? "100%" : 700,
                    maxWidth: isMobile ? "100%" : "95vw",
                    m: isMobile ? 0 : 2,
                    borderRadius: isMobile ? 0 : "20px",
                    bgcolor: "#F0F0F0",
                    overflow: isMobile ? "auto": "hidden",
                },
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    p: {xs: 2, sm: 2.5},
                    pt: {xs: 6, sm: 2.5},
                    minHeight: isMobile ? "100vh" : "auto",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: {xs: 12, sm: 10},
                        right: {xs: 12, sm: 10},
                        width: 42.11,
                        height: 40.88,
                        borderRadius: "30px",
                        bgcolor: "#FBFBFB",
                        "&:hover": {bgcolor: "#FBFBFB"},
                    }}
                >
                    <CloseIcon sx={{color: "#111111"}}/>
                </IconButton>
                <Box
                    component="form"
                    onSubmit={formik.handleSubmit}
                >
                    <Typography
                        sx={{
                            fontFamily: "'Zen Maru Gothic', sans-serif",
                            fontWeight: 900,
                            fontSize: {xs: 20, sm: 24},
                            textTransform: "uppercase",
                            color: "#111",
                            mb:2
                        }}
                    >
                        {t("CREATE ACCOUNT")}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: {xs: "column", sm: "row"},
                            gap: {xs: 1, sm: 0,md:2},
                            mb:3,
                            justifyContent: "flex-start",
                            alignItems: {xs: "flex-start", sm: "center"},
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "'Roboto Condensed', sans-serif",
                                fontSize: {xs: 13, sm: 14},
                                lineHeight: "120%",
                            }}
                        >
                            {t("Do you have an account already?")}
                        </Typography>
                        <Link
                            component="button"
                            type="button"
                            onClick={()=>handleClose()}
                            underline="always"
                            sx={{
                                color: "#E5007E",
                                textDecoration: "underline",
                                cursor: "pointer",
                                fontWeight: 700,
                                fontFamily: "'Roboto Condensed', sans-serif",
                                fontSize: {xs: 14, sm: 16},
                                p: 0,
                                textAlign: "left",
                            }}
                        >
                            {t("Log in here")}
                        </Link>
                    </Box>


                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            // gap: 3,
                            // mb: 6,
                        }}
                    >

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                gap: { xs: 2, md: 4 },
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: "100%", md: FIELD_WIDTH },
                                    flexShrink: 0,
                                }}
                            >
                                <ProfileTextField
                                    name="name"
                                    label={t("Name")}
                                    required
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                            </Box>
                            <Box
                                sx={{
                                    width: { xs: "100%", md: FIELD_WIDTH },
                                    flexShrink: 0,
                                }}
                            >
                                <ProfileTextField
                                    name="afm"
                                    label={t("AFM")}
                                    required
                                    value={formik.values.afm}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                gap: { xs: 2, md: 4 },
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: "100%", md: FIELD_WIDTH },
                                    flexShrink: 0,
                                }}
                            >
                                <ProfileTextField
                                    name="company"
                                    label={t("Company")}
                                    value={formik.values.company}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                            </Box>
                            <Box
                                sx={{
                                    width: { xs: "100%", md: FIELD_WIDTH },
                                    flexShrink: 0,
                                }}
                            >
                                <ProfileTextField
                                    name="email"
                                    label={t("Email")}
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                gap: { xs: 2, md: 4 },
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: "100%", md: FIELD_WIDTH },
                                    flexShrink: 0,
                                }}
                            >
                                <ProfileSelectField
                                    name="country"
                                    label={t("Country")}
                                    value={formik.values.country}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    options={countryOptions}
                                    required
                                />
                            </Box>
                            <Box
                                sx={{
                                    width: { xs: "100%", md: FIELD_WIDTH },
                                    flexShrink: 0,
                                }}
                            >
                                <ProfileTextField
                                    name="phone"
                                    label={t("Phone")}
                                    value={formik.values.phone}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                gap: { xs: 2, md: 4 },
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: "100%", md: FIELD_WIDTH },
                                    flexShrink: 0,
                                }}
                            >
                                <ProfileTextField
                                    name="password"
                                    label={t("Password")}
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    required
                                />
                            </Box>
                            <Box
                                sx={{
                                    width: { xs: "100%", md: FIELD_WIDTH },
                                    flexShrink: 0,
                                }}
                            >
                                <ProfileTextField
                                    name="confirmPassword"
                                    label={t("Confirm Password")}
                                    value={formik.values.confirmPassword}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    required
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2.5,
                        }}
                    >
                        <Box
                            sx={{
                                mt: 2,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <SquareCheckbox
                                        checked={formik.values.newsletterOptIn || false}
                                        onChange={(e) =>
                                            formik.setFieldValue("newsletterOptIn", e.target.checked)
                                        }
                                        sx={{
                                            p: 0,
                                            mr: 1,
                                            "& .MuiSvgIcon-root": { fontSize: 18 },
                                        }}
                                    />
                                }
                                label={t("Register to our newsletter")}
                                sx={{
                                    m: 0,
                                    "& .MuiTypography-root": {
                                        fontFamily: "'Roboto Condensed', sans-serif",
                                        fontSize: 14,
                                        color: "#111111",
                                    },
                                }}
                            />

                            <FormControlLabel
                                control={
                                    <SquareCheckbox
                                        checked={formik.values.promotionsOptIn || false}
                                        onChange={(e) =>
                                            formik.setFieldValue("promotionsOptIn", e.target.checked)
                                        }
                                        sx={{
                                            p: 0,
                                            mr: 1,
                                            "& .MuiSvgIcon-root": { fontSize: 18 },
                                        }}
                                    />
                                }
                                label={t("Receive special offers from our partners!")}
                                sx={{
                                    m: 0,
                                    "& .MuiTypography-root": {
                                        fontFamily: "'Roboto Condensed', sans-serif",
                                        fontSize: 14,
                                        color: "#111111",
                                    },
                                }}
                            />
                            <FormControl

                            >
                                <FormControlLabel
                                    control={
                                        <SquareCheckbox
                                            checked={formik.values.terms || false}
                                            onChange={(e) =>
                                                formik.setFieldValue("terms", e.target.checked)
                                            }
                                            sx={{
                                                p: 0,
                                                mr: 1,
                                                "& .MuiSvgIcon-root": { fontSize: 18 },
                                            }}
                                        />
                                    }
                                    label={t("Read and confirm the terms and conditions and privacy policy")}
                                    sx={{
                                        m: 0,
                                        "& .MuiTypography-root": {
                                            fontFamily: "'Roboto Condensed', sans-serif",
                                            fontSize: 14,
                                            color: "#111111",
                                        },
                                    }}
                                />
                                <FormHelperText>
                                    <ErrorMessage name={"terms"}/>
                                </FormHelperText>
                            </FormControl>

                        </Box>

                        <Box sx={{ mt: 3 }}>
                            <LoadingButton
                                loading={loading}
                                type={'submit'}
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: 2.5,
                                    py: 1,
                                    height: 37,
                                    borderRadius: "120px",
                                    backgroundColor: "#E5007E",
                                    fontFamily: "'Zen Maru Gothic', sans-serif",
                                    fontWeight: 900,
                                    fontSize: 14,
                                    textTransform: "uppercase",
                                    color: "#FCFCFC",
                                    "&:hover": {
                                        backgroundColor: "#c9006b",
                                    },
                                }}
                            >
                                {t('Save')}
                            </LoadingButton>
                        </Box>
                    </Box>

                </Box>
            </Box>
        </Dialog>
        </FormikProvider>
    );
}
